import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  CustomInput,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomPagination from "components/Common/CustomPagination";
import Swal from "sweetalert2";
import {
  getAllRoles,
  addRole,
  updateRole,
  deleteRole,
  toggleRoleStatus,
  getAllPermissions,
  getAdminUsers,
  addAdminUser,
  updateAdminUser,
  deleteAdminUser,
  toggleAdminUserStatus,
} from "../../api/ApiServices";
import {
  FaEdit,
  FaTrash,
  FaShieldAlt,
  FaUsers,
  FaUserPlus,
  FaUser,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const ITEMS_PER_PAGE = 10;

const RoleManagement = () => {
  // Active Tab: "roles" or "admins"
  const [activeTab, setActiveTab] = useState("roles");

  // Determine if logged-in user is Super Admin
  const isSuperAdmin = (() => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      if (userData?.isSuperAdmin === true) return true;
      if (userData?.isSuperAdmin === false) return false;
      const role = (
        localStorage.getItem("userRole") ||
        userData?.roleName ||
        userData?.type ||
        ""
      )
        .toLowerCase()
        .trim();
      if (
        role === "super admin" ||
        role === "superadmin" ||
        role === "super_admin" ||
        role.includes("super")
      ) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  })();

  /* =====================================================
     1. ROLES STATE
  ===================================================== */
  const [roles, setRoles] = useState([]);
  const [allRolesList, setAllRolesList] = useState([]); // for dropdowns
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Role Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    permissions: [],
  });

  const [validationErrors, setValidationErrors] = useState({
    name: "",
  });

  // View Role Modal State
  const [viewRoleModalOpen, setViewRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  /* =====================================================
     2. ADMIN USERS STATE
  ===================================================== */
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSearchTerm, setAdminSearchTerm] = useState("");
  const [adminRoleFilter, setAdminRoleFilter] = useState("all");
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [adminTotalItems, setAdminTotalItems] = useState(0);
  const [adminTotalPages, setAdminTotalPages] = useState(1);

  // Admin User Modal State
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminEditMode, setAdminEditMode] = useState(false);
  const [editingAdminUuid, setEditingAdminUuid] = useState(null);

  const [adminFormData, setAdminFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    mobileNumber: "",
    country: "India",
    address: "",
    roleUuid: "",
  });

  const [adminValidationErrors, setAdminValidationErrors] = useState({});

  // View Admin Modal State
  const [viewAdminModalOpen, setViewAdminModalOpen] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);

  /* =====================================================
     FETCH PERMISSIONS & ROLES
  ===================================================== */
  const fetchPermissions = async () => {
    try {
      const res = await getAllPermissions();
      if (res?.data) {
        setPermissions(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    }
  };

  const fetchRoles = async (page = 1, search = "", status = "") => {
    try {
      setLoading(true);
      const res = await getAllRoles(page, ITEMS_PER_PAGE, search, status);
      if (res?.data) {
        const filteredRoles = res.data.filter(
          (r) => !r.name.toLowerCase().includes("super")
        );
        setRoles(filteredRoles);
        setTotalItems(filteredRoles.length);
        setTotalPages(res.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRolesForDropdown = async () => {
    try {
      const res = await getAllRoles(1, 100, "", "");
      if (res?.data) {
        const filteredRoles = res.data.filter(
          (r) => !r.name.toLowerCase().includes("super")
        );
        setAllRolesList(filteredRoles);
      }
    } catch (err) {
      console.error("Failed to fetch all roles for dropdown:", err);
    }
  };

  /* =====================================================
     FETCH ADMIN USERS
  ===================================================== */
  const fetchAdminUsers = async (page = 1, search = "", roleUuid = "") => {
    try {
      setAdminLoading(true);
      const filterUuid = roleUuid === "all" ? "" : roleUuid;
      const res = await getAdminUsers(page, ITEMS_PER_PAGE, search, filterUuid);
      if (res?.data) {
        setAdminUsers(res.data);
        setAdminTotalItems(res.pagination?.totalAdmins || res.data.length);
        setAdminTotalPages(res.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch admin users:", err);
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchAllRolesForDropdown();
  }, []);

  useEffect(() => {
    if (activeTab === "roles") {
      fetchRoles(currentPage, searchTerm, statusFilter);
    } else {
      fetchAdminUsers(adminCurrentPage, adminSearchTerm, adminRoleFilter);
    }
  }, [
    activeTab,
    currentPage,
    searchTerm,
    statusFilter,
    adminCurrentPage,
    adminSearchTerm,
    adminRoleFilter,
  ]);

  /* =====================================================
     ROLE MODAL CONTROLS
  ===================================================== */
  const openAddModal = () => {
    setEditMode(false);
    setEditingUuid(null);
    setFormData({
      name: "",
      permissions: [],
    });
    setValidationErrors({ name: "" });
    setModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditMode(true);
    setEditingUuid(role.uuid);
    setFormData({
      name: role.name || "",
      permissions: role.permissions || [],
    });
    setValidationErrors({ name: "" });
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handlePermissionToggle = (permIdentifier) => {
    setFormData((prev) => {
      const exists = prev.permissions.includes(permIdentifier);
      const newPerms = exists
        ? prev.permissions.filter((p) => p !== permIdentifier)
        : [...prev.permissions, permIdentifier];
      return { ...prev, permissions: newPerms };
    });
  };

  const handleSelectAll = () => {
    const allIdentifiers = permissions.map((p) => p.uuid);
    setFormData((prev) => ({
      ...prev,
      permissions: allIdentifiers,
    }));
  };

  const handleDeselectAll = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: [],
    }));
  };

  const validateRoleForm = () => {
    let isValid = true;
    const errors = { name: "" };

    if (!formData.name.trim()) {
      errors.name = "Role name is required";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!validateRoleForm()) return;

    try {
      if (editMode) {
        await updateRole(editingUuid, {
          name: formData.name.trim(),
          permissions: formData.permissions,
        });
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Role updated successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const payload = {
          name: formData.name.trim(),
          permissions: formData.permissions,
          adminUuid: userData?.uuid || userData?._id,
        };

        await addRole(payload);
        Swal.fire({
          icon: "success",
          title: "Created",
          text: "Role created successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setModalOpen(false);
      fetchRoles(currentPage, searchTerm, statusFilter);
      fetchAllRolesForDropdown();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text: err?.response?.data?.message || err?.message || "Failed to save role",
      });
    }
  };

  const handleToggleStatus = async (uuid) => {
    try {
      await toggleRoleStatus(uuid);
      fetchRoles(currentPage, searchTerm, statusFilter);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || err?.message || "Failed to toggle status",
      });
    }
  };

  const handleDelete = async (uuid) => {
    const confirm = await Swal.fire({
      title: "Delete Role?",
      text: "Are you sure you want to delete this role?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteRole(uuid);
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Role has been removed",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchRoles(currentPage, searchTerm, statusFilter);
        fetchAllRolesForDropdown();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || err?.message || "Failed to delete role",
        });
      }
    }
  };

  /* =====================================================
     ADMIN USER MODAL CONTROLS
  ===================================================== */
  const openAddAdminModal = () => {
    setAdminEditMode(false);
    setEditingAdminUuid(null);
    const subAdminRoles = allRolesList.filter(
      (r) => !r.name.toLowerCase().includes("super")
    );
    setAdminFormData({
      fullName: "",
      email: "",
      password: "",
      mobileNumber: "",
      country: "India",
      address: "",
      roleUuid: subAdminRoles[0]?.uuid || "",
    });
    setAdminValidationErrors({});
    setAdminModalOpen(true);
  };

  const openEditAdminModal = (user) => {
    setAdminEditMode(true);
    setEditingAdminUuid(user.uuid);
    setAdminFormData({
      fullName: user.fullName || "",
      email: user.email || "",
      password: "", // empty unless updating
      mobileNumber: user.mobileNumber || "",
      country: user.country || "India",
      address: user.address || "",
      roleUuid: user.roleUuid || "",
    });
    setAdminValidationErrors({});
    setAdminModalOpen(true);
  };

  const toggleAdminModal = () => {
    setAdminModalOpen(!adminModalOpen);
  };

  const openViewRoleModal = (role) => {
    setSelectedRole(role);
    setViewRoleModalOpen(true);
  };

  const openViewAdminModal = (user) => {
    setSelectedAdminUser(user);
    setViewAdminModalOpen(true);
  };

  const validateAdminForm = () => {
    const errors = {};
    if (!adminFormData.fullName.trim()) {
      errors.fullName = "Full name is required";
    }
    if (!adminFormData.email.trim()) {
      errors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(adminFormData.email.trim())
    ) {
      errors.email = "Invalid email format";
    }
    if (!adminEditMode) {
      if (!adminFormData.password || adminFormData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
    } else if (adminFormData.password && adminFormData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    if (!adminFormData.roleUuid) {
      errors.roleUuid = "Please select a role";
    }

    setAdminValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdminFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateAdminForm()) return;

    try {
      if (adminEditMode) {
        const payload = {
          fullName: adminFormData.fullName.trim(),
          email: adminFormData.email.trim(),
          mobileNumber: adminFormData.mobileNumber,
          country: adminFormData.country,
          address: adminFormData.address,
          roleUuid: adminFormData.roleUuid,
        };
        if (adminFormData.password && adminFormData.password.trim()) {
          payload.password = adminFormData.password.trim();
        }

        await updateAdminUser(editingAdminUuid, payload);
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Admin user updated successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await addAdminUser({
          fullName: adminFormData.fullName.trim(),
          email: adminFormData.email.trim(),
          password: adminFormData.password.trim(),
          mobileNumber: adminFormData.mobileNumber,
          country: adminFormData.country,
          address: adminFormData.address,
          roleUuid: adminFormData.roleUuid,
        });
        Swal.fire({
          icon: "success",
          title: "Created",
          text: "Admin user created successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setAdminModalOpen(false);
      fetchAdminUsers(adminCurrentPage, adminSearchTerm, adminRoleFilter);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text: err?.response?.data?.message || err?.message || "Failed to save admin user",
      });
    }
  };

  const handleToggleAdminStatus = async (uuid) => {
    try {
      await toggleAdminUserStatus(uuid);
      fetchAdminUsers(adminCurrentPage, adminSearchTerm, adminRoleFilter);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || err?.message || "Failed to toggle status",
      });
    }
  };

  const handleDeleteAdmin = async (uuid) => {
    const confirm = await Swal.fire({
      title: "Delete Admin User?",
      text: "Are you sure you want to remove this admin user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteAdminUser(uuid);
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Admin user has been removed",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchAdminUsers(adminCurrentPage, adminSearchTerm, adminRoleFilter);
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || err?.message || "Failed to delete admin user",
        });
      }
    }
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow border-0">
              <CardHeader className="bg-white border-0 pb-0 pt-4">
                <Row className="align-items-center mb-3">
                  <Col>
                    <h3 className="mb-0 font-weight-bold text-dark d-flex align-items-center gap-2">
                      <FaShieldAlt className="text-primary mr-2" />
                      Role & User Management
                    </h3>
                  </Col>
                  <Col className="text-right">
                    {activeTab === "roles" ? (
                      <Button
                        size="sm"
                        onClick={openAddModal}
                        className="bg-button-gradient-primary text-white"
                      >
                        + Add New Role
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={openAddAdminModal}
                        className="bg-button-gradient-primary text-white"
                      >
                        <FaUserPlus className="mr-1" /> + Add Admin User
                      </Button>
                    )}
                  </Col>
                </Row>

                {/* Tab Navigation */}
                <Nav pills className="nav-fill flex-row p-1 bg-secondary rounded mb-3" style={{ maxWidth: "360px" }}>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer font-weight-bold py-2 ${
                        activeTab === "roles" ? "active bg-primary text-white shadow-sm" : "text-dark"
                      }`}
                      onClick={() => setActiveTab("roles")}
                      style={{ borderRadius: "6px" }}
                    >
                      <FaShieldAlt className="mr-2" /> Roles ({totalItems})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`cursor-pointer font-weight-bold py-2 ${
                        activeTab === "admins" ? "active bg-primary text-white shadow-sm" : "text-dark"
                      }`}
                      onClick={() => setActiveTab("admins")}
                      style={{ borderRadius: "6px" }}
                    >
                      <FaUsers className="mr-2" /> Admin Users ({adminTotalItems})
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>

              <CardBody>
                {/* =====================================================
                    TAB 1: ROLES
                ===================================================== */}
                {activeTab === "roles" && (
                  <>
                    {/* Search & Filter */}
                    <Row
                      className="align-items-start align-items-lg-center mb-4 px-3 flex-column flex-lg-row"
                      style={{ gap: "12px" }}
                    >
                      <Col lg="5" className="px-0">
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="fas fa-search" />
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="search"
                            placeholder="Search role by name..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                          />
                        </InputGroup>
                      </Col>

                      <Col lg="3" className="px-0">
                        <Input
                          type="select"
                          value={statusFilter}
                          onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          <option value="all">All Statuses</option>
                          <option value="true">Active Only</option>
                          <option value="false">Inactive Only</option>
                        </Input>
                      </Col>
                    </Row>

                    {/* Table */}
                    {loading ? (
                      <div className="text-center p-5">
                        <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-items-center table-flush" responsive>
                          <thead className="thead-light">
                            <tr>
                              <th>Sr.No</th>
                              <th>Role Name</th>
                              <th>Assigned Permissions</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {roles.length > 0 ? (
                              roles.map((role, index) => (
                                <tr key={role.uuid || role._id}>
                                  <td>{index + 1 + (currentPage - 1) * ITEMS_PER_PAGE}</td>
                                  <td className="font-weight-bold text-dark">
                                    {role.name}
                                  </td>
                                  <td>
                                    <Badge color="info" pill className="p-2">
                                      {role.permissions?.length || 0} Modules
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge
                                      color={role.isActive ? "success" : "danger"}
                                      className={isSuperAdmin ? "cursor-pointer" : ""}
                                      onClick={() =>
                                        isSuperAdmin && handleToggleStatus(role.uuid)
                                      }
                                      title={
                                        isSuperAdmin
                                          ? "Click to toggle status"
                                          : "Role status"
                                      }
                                    >
                                      {role.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => openViewRoleModal(role)}
                                        title="View Role Details"
                                      >
                                        <FaEye />
                                      </Button>
                                      {isSuperAdmin && (
                                        <>
                                          <Button
                                            color="warning"
                                            size="sm"
                                            onClick={() => openEditModal(role)}
                                            title="Edit Role"
                                          >
                                            <FaEdit />
                                          </Button>
                                          <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => handleDelete(role.uuid)}
                                            title="Delete Role"
                                          >
                                            <FaTrash />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="text-center py-4 text-muted"
                                >
                                  No roles found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Pagination */}
                    {!loading && roles.length > 0 && (
                      <CardFooter className="py-4 border-0">
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          totalItems={totalItems}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={(p) => setCurrentPage(p)}
                        />
                      </CardFooter>
                    )}
                  </>
                )}

                {/* =====================================================
                    TAB 2: ADMIN USERS
                ===================================================== */}
                {activeTab === "admins" && (
                  <>
                    {/* Search & Filter */}
                    <Row
                      className="align-items-start align-items-lg-center mb-4 px-3 flex-column flex-lg-row"
                      style={{ gap: "12px" }}
                    >
                      <Col lg="5" className="px-0">
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="fas fa-search" />
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="search"
                            placeholder="Search by name, email, or mobile..."
                            value={adminSearchTerm}
                            onChange={(e) => {
                              setAdminSearchTerm(e.target.value);
                              setAdminCurrentPage(1);
                            }}
                          />
                        </InputGroup>
                      </Col>

                      <Col lg="3" className="px-0">
                        <Input
                          type="select"
                          value={adminRoleFilter}
                          onChange={(e) => {
                            setAdminRoleFilter(e.target.value);
                            setAdminCurrentPage(1);
                          }}
                        >
                          <option value="all">All Roles</option>
                          {allRolesList
                            .filter((r) => !r.name.toLowerCase().includes("super"))
                            .map((r) => (
                              <option key={r.uuid} value={r.uuid}>
                                {r.name}
                              </option>
                            ))}
                        </Input>
                      </Col>
                    </Row>

                    {/* Table */}
                    {adminLoading ? (
                      <div className="text-center p-5">
                        <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-items-center table-flush" responsive>
                          <thead className="thead-light">
                            <tr>
                              <th>Sr.No</th>
                              <th>Full Name</th>
                              <th>Email</th>
                              <th>Mobile</th>
                              <th>Assigned Role</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminUsers.length > 0 ? (
                              adminUsers.map((user, index) => (
                                <tr key={user.uuid || user._id}>
                                  <td>
                                    {index + 1 + (adminCurrentPage - 1) * ITEMS_PER_PAGE}
                                  </td>
                                  <td className="font-weight-bold text-dark">
                                    {user.fullName}
                                  </td>
                                  <td>{user.email}</td>
                                  <td>{user.mobileNumber || "—"}</td>
                                  <td>
                                    <Badge
                                      color={
                                        (user.roleName || "").toLowerCase().includes("super")
                                          ? "primary"
                                          : "info"
                                      }
                                      pill
                                      className="p-2"
                                    >
                                      {user.roleName || user.type || "admin"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge
                                      color={user.isActive ? "success" : "danger"}
                                      className={isSuperAdmin ? "cursor-pointer" : ""}
                                      onClick={() =>
                                        isSuperAdmin && handleToggleAdminStatus(user.uuid)
                                      }
                                      title={
                                        isSuperAdmin
                                          ? "Click to toggle status"
                                          : "Admin status"
                                      }
                                    >
                                      {user.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => openViewAdminModal(user)}
                                        title="View Admin User"
                                      >
                                        <FaEye />
                                      </Button>
                                      {isSuperAdmin && (
                                        <>
                                          <Button
                                            color="warning"
                                            size="sm"
                                            onClick={() => openEditAdminModal(user)}
                                            title="Edit Admin User"
                                          >
                                            <FaEdit />
                                          </Button>
                                          <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => handleDeleteAdmin(user.uuid)}
                                            title="Delete Admin User"
                                          >
                                            <FaTrash />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="text-center py-4 text-muted"
                                >
                                  No admin users found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Pagination */}
                    {!adminLoading && adminUsers.length > 0 && (
                      <CardFooter className="py-4 border-0">
                        <CustomPagination
                          currentPage={adminCurrentPage}
                          totalPages={adminTotalPages}
                          totalItems={adminTotalItems}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={(p) => setAdminCurrentPage(p)}
                        />
                      </CardFooter>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>

      {/* =====================================================
          ROLE MODAL (Add / Edit Role)
      ===================================================== */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal} className="border-bottom-0 pb-0">
          <h4 className="font-weight-bold mb-0">
            {editMode ? "Edit Role" : "Add New Role"}
          </h4>
        </ModalHeader>
        <Form onSubmit={handleRoleSubmit}>
          <ModalBody className="pt-2">
            <FormGroup>
              <Label className="font-weight-bold small">
                Role Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="e.g. Content Manager, Support Staff..."
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (validationErrors.name) setValidationErrors({ name: "" });
                }}
                invalid={!!validationErrors.name}
              />
              {validationErrors.name && (
                <small className="text-danger">{validationErrors.name}</small>
              )}
            </FormGroup>

            {/* Sidebar Module Permissions Checkboxes */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label className="font-weight-bold small mb-0">
                  Assign Module Permissions (Sidebar Modules)
                </Label>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleDeselectAll}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div
                className="p-3 border rounded"
                style={{
                  maxHeight: "340px",
                  overflowY: "auto",
                  backgroundColor: "#f8f9fe",
                }}
              >
                <Row>
                  {permissions.map((perm) => {
                    const isChecked =
                      formData.permissions.includes(perm.uuid) ||
                      formData.permissions.includes(perm.name) ||
                      formData.permissions.includes(perm.key);

                    return (
                      <Col md="6" lg="4" key={perm.uuid} className="py-2">
                        <CustomInput
                          type="checkbox"
                          id={`perm-${perm.uuid}`}
                          label={perm.name}
                          checked={isChecked}
                          onChange={() => handlePermissionToggle(perm.uuid)}
                        />
                      </Col>
                    );
                  })}
                </Row>
              </div>
              <small className="text-muted mt-2 d-block">
                Selected: {formData.permissions.length} of {permissions.length} modules
              </small>
            </div>
          </ModalBody>
          <ModalFooter className="border-top-0 pt-0">
            <Button color="secondary" type="button" onClick={toggleModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editMode ? "Update Role" : "Create Role"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* =====================================================
          ADMIN USER MODAL (Add / Edit Admin User)
      ===================================================== */}
      <Modal isOpen={adminModalOpen} toggle={toggleAdminModal} size="md" centered>
        <ModalHeader toggle={toggleAdminModal} className="border-bottom-0 pb-0">
          <h4 className="font-weight-bold mb-0">
            {adminEditMode ? "Edit Admin User" : "Add New Admin User"}
          </h4>
        </ModalHeader>
        <Form onSubmit={handleAdminFormSubmit}>
          <ModalBody className="pt-2">
            <FormGroup>
              <Label className="font-weight-bold small">
                Full Name <span className="text-danger">*</span>
              </Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <FaUser size={13} />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={adminFormData.fullName}
                  onChange={(e) =>
                    setAdminFormData({ ...adminFormData, fullName: e.target.value })
                  }
                  invalid={!!adminValidationErrors.fullName}
                />
              </InputGroup>
              {adminValidationErrors.fullName && (
                <small className="text-danger">{adminValidationErrors.fullName}</small>
              )}
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small">
                Email Address <span className="text-danger">*</span>
              </Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <FaEnvelope size={13} />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="email"
                  placeholder="e.g. admin@example.com"
                  value={adminFormData.email}
                  onChange={(e) =>
                    setAdminFormData({ ...adminFormData, email: e.target.value })
                  }
                  invalid={!!adminValidationErrors.email}
                />
              </InputGroup>
              {adminValidationErrors.email && (
                <small className="text-danger">{adminValidationErrors.email}</small>
              )}
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small">
                {adminEditMode ? "Password (leave blank to keep current)" : "Password *"}
              </Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <FaLock size={13} />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="password"
                  placeholder={adminEditMode ? "••••••••" : "Minimum 6 characters"}
                  value={adminFormData.password}
                  onChange={(e) =>
                    setAdminFormData({ ...adminFormData, password: e.target.value })
                  }
                  invalid={!!adminValidationErrors.password}
                />
              </InputGroup>
              {adminValidationErrors.password && (
                <small className="text-danger">{adminValidationErrors.password}</small>
              )}
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small">Mobile Number</Label>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <FaPhone size={13} />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="tel"
                  placeholder="e.g. +91 9876543210"
                  value={adminFormData.mobileNumber}
                  onChange={(e) =>
                    setAdminFormData({ ...adminFormData, mobileNumber: e.target.value })
                  }
                />
              </InputGroup>
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small">
                Assign Role <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={adminFormData.roleUuid}
                onChange={(e) =>
                  setAdminFormData({ ...adminFormData, roleUuid: e.target.value })
                }
                invalid={!!adminValidationErrors.roleUuid}
              >
                <option value="">-- Select a Role --</option>
                {allRolesList
                  .filter((r) => !r.name.toLowerCase().includes("super"))
                  .map((r) => (
                    <option key={r.uuid} value={r.uuid}>
                      {r.name} ({r.permissions?.length || 0} Modules)
                    </option>
                  ))}
              </Input>
              {adminValidationErrors.roleUuid && (
                <small className="text-danger">{adminValidationErrors.roleUuid}</small>
              )}
            </FormGroup>

            <FormGroup>
              <Label className="font-weight-bold small">Address</Label>
              <Input
                type="text"
                placeholder="e.g. Mohali, Punjab"
                value={adminFormData.address}
                onChange={(e) =>
                  setAdminFormData({ ...adminFormData, address: e.target.value })
                }
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter className="border-top-0 pt-0">
            <Button color="secondary" type="button" onClick={toggleAdminModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {adminEditMode ? "Update Admin" : "Create Admin User"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* =====================================================
          VIEW ROLE MODAL (Read-Only Role Details & Permissions)
      ===================================================== */}
      <Modal
        isOpen={viewRoleModalOpen}
        toggle={() => setViewRoleModalOpen(!viewRoleModalOpen)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setViewRoleModalOpen(!viewRoleModalOpen)}
          className="border-bottom pb-3"
        >
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-2"
              style={{ width: "36px", height: "36px" }}
            >
              <FaShieldAlt size={16} />
            </div>
            <div>
              <h4 className="font-weight-bold mb-0">Role Details</h4>
              <small className="text-muted">
                View role information and module access permissions
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="py-4">
          {selectedRole ? (
            <>
              {/* Role Summary Header */}
              <div
                className="p-3 mb-4 rounded border"
                style={{ backgroundColor: "#f8f9fe" }}
              >
                <Row className="align-items-center">
                  <Col md="6">
                    <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                      Role Name
                    </small>
                    <h3 className="font-weight-bold text-dark mb-0">
                      {selectedRole.name}
                    </h3>
                  </Col>
                  <Col md="3">
                    <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                      Status
                    </small>
                    <Badge
                      color={selectedRole.isActive ? "success" : "danger"}
                      pill
                      className="px-3 py-1 font-weight-bold"
                    >
                      {selectedRole.isActive ? "● Active" : "● Inactive"}
                    </Badge>
                  </Col>
                  <Col md="3">
                    <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                      Access Scope
                    </small>
                    <Badge color="info" pill className="px-3 py-1 font-weight-bold">
                      {selectedRole.permissions?.length || 0} Modules Assigned
                    </Badge>
                  </Col>
                </Row>
              </div>

              {/* Sidebar Module Permissions Grid */}
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <h5 className="font-weight-bold text-dark mb-0">
                  Sidebar Module Permissions
                </h5>
                <small className="text-muted">
                  Total System Modules: {permissions.length}
                </small>
              </div>

              <div
                className="p-3 border rounded"
                style={{
                  maxHeight: "360px",
                  overflowY: "auto",
                  backgroundColor: "#ffffff",
                }}
              >
                {permissions.length > 0 ? (
                  <Row>
                    {permissions.map((perm) => {
                      const isAssigned =
                        selectedRole.permissions?.includes(perm.uuid) ||
                        selectedRole.permissions?.includes(perm.name) ||
                        selectedRole.permissions?.includes(perm.key);

                      return (
                        <Col md="6" key={perm.uuid || perm.name} className="py-2">
                          <div
                            className={`d-flex align-items-center justify-content-between p-2 rounded border ${
                              isAssigned
                                ? "border-success bg-light text-dark"
                                : "border-light text-muted"
                            }`}
                            style={{
                              backgroundColor: isAssigned ? "#f0fff4" : "#fcfcfc",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <div className="d-flex align-items-center">
                              {isAssigned ? (
                                <FaCheckCircle
                                  className="text-success mr-2"
                                  size={16}
                                />
                              ) : (
                                <FaTimesCircle
                                  className="text-muted mr-2"
                                  size={16}
                                  style={{ opacity: 0.4 }}
                                />
                              )}
                              <span
                                className={`small ${
                                  isAssigned
                                    ? "font-weight-bold text-dark"
                                    : "text-muted"
                                }`}
                              >
                                {perm.name}
                              </span>
                            </div>
                            <Badge
                              color={isAssigned ? "success" : "secondary"}
                              pill
                              className="px-2 py-1"
                              style={{ fontSize: "10px" }}
                            >
                              {isAssigned ? "Granted" : "No Access"}
                            </Badge>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="text-center py-4 text-muted small">
                    No permissions found in system.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-muted">No role selected</div>
          )}
        </ModalBody>
        <ModalFooter className="border-top pt-3">
          <Button
            color="secondary"
            onClick={() => setViewRoleModalOpen(false)}
          >
            Close
          </Button>
          {isSuperAdmin && selectedRole && (
            <Button
              color="warning"
              onClick={() => {
                setViewRoleModalOpen(false);
                openEditModal(selectedRole);
              }}
            >
              <FaEdit className="mr-1" /> Edit Role
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* =====================================================
          VIEW ADMIN USER MODAL
      ===================================================== */}
      <Modal
        isOpen={viewAdminModalOpen}
        toggle={() => setViewAdminModalOpen(!viewAdminModalOpen)}
        size="md"
        centered
      >
        <ModalHeader
          toggle={() => setViewAdminModalOpen(!viewAdminModalOpen)}
          className="border-bottom pb-3"
        >
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-2"
              style={{ width: "36px", height: "36px" }}
            >
              <FaUser size={16} />
            </div>
            <div>
              <h4 className="font-weight-bold mb-0">Admin User Details</h4>
              <small className="text-muted">
                View profile and role credentials
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="py-4">
          {selectedAdminUser ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center p-3 rounded bg-light mb-3">
                <div
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center font-weight-bold mr-3 shadow-sm"
                  style={{ width: "48px", height: "48px", fontSize: "18px" }}
                >
                  {selectedAdminUser.fullName?.[0]?.toUpperCase() || "A"}
                </div>
                <div>
                  <h4 className="mb-0 font-weight-bold text-dark">
                    {selectedAdminUser.fullName}
                  </h4>
                  <small className="text-muted">{selectedAdminUser.email}</small>
                </div>
              </div>

              <div className="p-3 border rounded">
                <Row className="mb-2">
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Role:
                  </Col>
                  <Col sm="8">
                    <Badge color="primary" pill className="px-3 py-1">
                      {selectedAdminUser.roleName || "Sub Admin"}
                    </Badge>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Mobile:
                  </Col>
                  <Col sm="8" className="small text-dark font-weight-bold">
                    {selectedAdminUser.mobileNumber || "—"}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Country:
                  </Col>
                  <Col sm="8" className="small text-dark">
                    {selectedAdminUser.country || "—"}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Address:
                  </Col>
                  <Col sm="8" className="small text-dark">
                    {selectedAdminUser.address || "—"}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Status:
                  </Col>
                  <Col sm="8">
                    <Badge
                      color={selectedAdminUser.isActive ? "success" : "danger"}
                      pill
                    >
                      {selectedAdminUser.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </Col>
                </Row>
                <Row>
                  <Col sm="4" className="text-muted small font-weight-bold">
                    Created:
                  </Col>
                  <Col sm="8" className="small text-muted">
                    {selectedAdminUser.createdAt
                      ? new Date(selectedAdminUser.createdAt).toLocaleDateString()
                      : "—"}
                  </Col>
                </Row>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">No admin selected</div>
          )}
        </ModalBody>
        <ModalFooter className="border-top pt-3">
          <Button
            color="secondary"
            onClick={() => setViewAdminModalOpen(false)}
          >
            Close
          </Button>
          {isSuperAdmin && selectedAdminUser && (
            <Button
              color="warning"
              onClick={() => {
                setViewAdminModalOpen(false);
                openEditAdminModal(selectedAdminUser);
              }}
            >
              <FaEdit className="mr-1" /> Edit Admin
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RoleManagement;
